import { Box, Flex, Text } from '@chakra-ui/react';
import {
  faChevronRight,
  faRotateRight,
  faSave
} from '@fortawesome/pro-regular-svg-icons';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ajvResolver } from '@hookform/resolvers/ajv';
import {
  Button,
  Modal,
  OutlineCard,
  useToast,
  Accordion
} from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { ISubmissionFormData, useFormContext } from 'src/contexts/FormContext';
import Keywords from './components/Keywords';
import FocusIssue from './components/FocusIssue';
import Title from './components/Title';
import Abstract from './components/Abstract';
import SupplementaryUpload from './components/UploadSupplement';
import { generateValidationSchema } from 'src/utils/validation';
import {
  applyRichTextSchemaKeywords,
  RICH_TEXT_AJV_KEYWORDS
} from 'src/utils/richTextSchemaValidation';
import StepHeader from './components/StepHeader';
import { SourceFileUpload } from './components/SourceFileUpload';
import { useDraftResponse } from 'src/contexts/uploadDraftProvider';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
import useFocusIssueConfig from 'src/hooks/useFocusIssue';
import { scrollToFirstError } from 'src/utils/scrollToFirstError';
import { useCallback, useEffect, useRef, useState } from 'react';
import DeleteDraft from './DeleteDraft';
import { checkFileUpload } from '..';
import BackToUpload from './BackToUpload';
import {
  CustomFormFieldComponent,
  FormFieldSingleFileUpload
} from 'src/components/FormField';
import StandardReportingGuidelines from './StandardReportingGuidelines';
import AdditionalSupplementaryUpload from './components/AdditionalSupplementaryUpload';
import { IDetailsStepConfig } from 'src/hooks/useSubmissionStepsConfig';
import { hasDuplicateKeywords } from 'src/utils/hasDuplicateKeywords';
import CoverLetter from './components/CoverLetter';
import Figures from './components/Figures';
import GraphicalAbstractImage from './components/GraphicalAbstractImage';
import { SidebarLayout } from 'src/layouts/SidebarLayout';
import { SubmissionSidebar } from './SubmissionSidebar';
import GenericRichTextInput from './components/common/GenericRichTextInput';
import FormFieldInteger from 'src/components/FormField/FormFieldInteger';
import Tables from './components/Tables';
import { useSubmissionLeaveGuardRegistration } from './SubmissionLeaveGuard';
import { scrollToPendingFileField } from './fileReplacementNavigation';
import {
  fetchExtractedSubmissionMetadata,
  fetchExtractedTitlePageMetadata,
  fetchUserProfileDetails
} from 'src/api/queries';
import {
  IExtractedSubmissionMetadata,
  ITitlePageExtractedSubmissionMetadata
} from '@publishing-engine/domain/models';
import MetadataExtraction, {
  MetadataExtractionPhase
} from './components/MetadataExtraction';

interface Props {
  currentStep: number;
  prevStep: number;
  totalSteps: number;
  isMetadataExtracted: boolean;
  draftSubmissionId: string;
  goToPreviousStep: () => void;
  goToNextStep: () => void;
  goToFirstStep: () => void;
  goToStep: (step: number) => void;
  autosaveDraftSubmission: (
    formData: ISubmissionFormData,
    submissionId?: string,
    draftStep?: number
  ) => Promise<{
    response: {
      isSuccess: boolean;
      hasRunExtraction?: boolean;
      draft?: Partial<ISubmissionFormData>;
    };
    data: {
      submissionId: string;
    };
  }>;
  saveDraftSubmission: (
    formData: ISubmissionFormData,
    submissionId?: string
  ) => void;
  stepConfigs: unknown;
}

const DetailsStep: React.FC<Props> = ({
  goToNextStep,
  goToFirstStep,
  saveDraftSubmission,
  currentStep,
  prevStep,
  totalSteps,
  autosaveDraftSubmission,
  draftSubmissionId,
  stepConfigs,
  goToStep
}) => {
  useEffect(() => scrollToPendingFileField('details'), []);
  const { formData, updateFormData } = useFormContext();
  const { submissionContext, countriesData } = useSubmissionContextData();
  const reviewSubmissionInformation =
    submissionContext?.config.cms.authorSubmissionForm
      .reviewSubmissionInformation;
  const submissionContextId = formData.submissionContext.submissionContextId;
  const draftContext = useDraftResponse();
  const setDraftResponse = draftContext?.setDraftResponse;
  const [isDisabledDraft, setIsDisableDraft] = useState<boolean>(false);
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const configs = stepConfigs as IDetailsStepConfig;

  const { validationOptions, labelOptions, guidelineLink } =
    useFocusIssueConfig(submissionContextId);

  if (configs.FocusIssue) {
    const field = configs.FocusIssue;
    configs.FocusIssue = {
      ...field,
      params: {
        labels: {
          ...field.params.labels,
          options: labelOptions
        },
        validations: {
          ...field.params.validations,
          options: validationOptions
        }
      }
    };
  }

  const isStandardReportingGuidelinesRequired =
    configs.StandardReportingGuidelines?.params.validations?.required ?? false;
  const schema = applyRichTextSchemaKeywords(generateValidationSchema(configs));

  const methods = useForm<ISubmissionFormData>({
    defaultValues: formData,
    resolver: ajvResolver(schema, { keywords: RICH_TEXT_AJV_KEYWORDS }),
    mode: 'onChange'
  });

  const { handleSubmit, getValues, setError, clearErrors, watch } = methods;
  const isDirty = methods.formState.isDirty;

  const runCustomValidations = (values: ISubmissionFormData) => {
    let hasValidationErrors = false;

    if (!validateFigures(values)) {
      hasValidationErrors = true;
    }
    if (!validateGraphicalAbstractImage(values)) {
      hasValidationErrors = true;
    }
    if (!validateTables(values)) {
      hasValidationErrors = true;
    }

    if (hasDuplicateKeywords(values.keywords)) {
      hasValidationErrors = true;
      setError('metadata.keywords', {
        type: 'manual',
        message: 'Duplicate values are not allowed'
      });
    }
    if (
      values?.englishKeywords &&
      hasDuplicateKeywords(values.englishKeywords)
    ) {
      hasValidationErrors = true;
      setError('metadata.englishKeywords', {
        type: 'manual',
        message: 'Duplicate values are not allowed'
      });
    }
    if (
      configs.StandardReportingGuidelinesDetails &&
      values.preference.standardReportingGuidelinesDetails &&
      values.preference.standardReportingGuidelines &&
      values.preference.standardReportingGuidelinesDetails.length === 0 &&
      isStandardReportingGuidelinesRequired
    ) {
      hasValidationErrors = true;
      setError('preference.standardReportingGuidelinesDetails', {
        type: 'manual',
        message: 'Please select at least one checkbox.'
      });
    }

    const needsArriveFile =
      values.preference.standardReportingGuidelinesDetails?.includes(
        'ARRIVE'
      ) &&
      (values.fileSet.ARRIVEFiles?.length ?? 0) === 0 &&
      isStandardReportingGuidelinesRequired;
    const needsConsortFile =
      values.preference.standardReportingGuidelinesDetails?.includes(
        'CONSORT Statement'
      ) &&
      (values.fileSet.CONSORTFiles?.length ?? 0) === 0 &&
      isStandardReportingGuidelinesRequired;

    if (needsArriveFile) {
      hasValidationErrors = true;
      setError('fileSet.ARRIVEFiles', {
        type: 'manual',
        message: 'Please upload a file to proceed further'
      });
    }
    if (needsConsortFile) {
      hasValidationErrors = true;
      setError('fileSet.CONSORTFiles', {
        type: 'manual',
        message: 'Please upload a file to proceed further'
      });
    }

    return !hasValidationErrors;
  };

  const updateFormDataFromDraftResponse = (response: {
    hasRunExtraction?: boolean;
    draft?: Partial<ISubmissionFormData>;
  }) => {
    const extractionStatus =
      typeof response.hasRunExtraction === 'boolean'
        ? { hasRunExtraction: response.hasRunExtraction }
        : {};

    if (response.draft) {
      updateFormData({ ...response.draft, ...extractionStatus });
      return;
    }

    if (response.hasRunExtraction !== undefined) {
      updateFormData(extractionStatus);
    }
  };

  const submitDraft = async (values: ISubmissionFormData) => {
    updateFormData(values);
    setDraftResponse?.({ status: 'pending' });
    setIsDisableDraft(true);
    const results = await autosaveDraftSubmission(values, draftSubmissionId);
    setIsDisableDraft(false);
    if (results.response.isSuccess) {
      setDraftResponse?.({ status: 'success' });
      updateFormDataFromDraftResponse(results.response);
      goToNextStep();
    } else {
      setDraftResponse?.({ status: 'error', data: results.response });
    }
  };

  const onSubmit = async () => {
    if (isNavigating) return;
    setIsNavigating(true);

    try {
      const values = getValuesForDraft();
      if (!runCustomValidations(values)) {
        scrollToFirstError();
        return;
      }

      await submitDraft(values);
    } finally {
      setIsNavigating(false);
    }
  };

  const onSaveDraft = () => {
    saveDraftSubmission(getValuesForDraft(), draftSubmissionId);
  };

  const saveCurrentProgress = useCallback(async () => {
    const values = getValuesForDraft();
    updateFormData(values);
    const results = await autosaveDraftSubmission(
      values,
      draftSubmissionId,
      currentStep + 2
    );

    if (results.response.isSuccess) {
      updateFormDataFromDraftResponse(results.response);
    }

    return results.response.isSuccess;
  }, [autosaveDraftSubmission, currentStep, draftSubmissionId, getValues, updateFormData]);

  const onNavigateSave = () => {
    void saveCurrentProgress();
  };

  const disableNextButton = () => {
    const values = getValues();
    if (values.fileSet.SourceFiles) {
      return values.fileSet.SourceFiles.some(file => !file.url);
    }
    return false;
  };
  const isDisableNextButton = disableNextButton();
  const checkFileIsUploading = () => {
    const values = getValues();
    return (
      Object.keys(values?.fileSet || {}).length > 0 &&
      checkFileUpload?.(values.fileSet)
    );
  };

  const isFileUploading = checkFileIsUploading();
  useSubmissionLeaveGuardRegistration({
    hasUnsavedChanges: isDirty,
    saveCurrentProgress
  });

  // ---------- Rerun Extraction ----------
  const toast = useToast();
  const [showRerunModal, setShowRerunModal] = useState(false);
  const [shouldRunExtraction, setShouldRunExtraction] = useState(false);
  // Track whether extraction was actually re-run after file changes
  const extractionWasReRun = useRef(false);
  // Keep the invalidated state across draft-save responses until extraction succeeds.
  const extractionNeedsRerun = useRef(formData.hasRunExtraction === false);
  const [extractionPhase, setExtractionPhase] =
    useState<MetadataExtractionPhase>('started');
  const [titlePageExtractionPhase, setTitlePageExtractionPhase] =
    useState<MetadataExtractionPhase>('started');
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [titlePageExtractionProgress, setTitlePageExtractionProgress] =
    useState(0);
  const [contentMismatch, setContentMismatch] = useState(false);
  const [missingInformation, setMissingInformation] = useState(false);

  // Store the original uploaded file URLs. Names are not unique, so they
  // cannot reliably tell us whether a file has been replaced.
  const originalManuscriptUrl = useRef(formData.fileSet.Manuscript?.url ?? '');
  const originalTitlePageUrl = useRef(formData.fileSet.TitlePage?.url ?? '');

  // Watch RHF form values reactively (FormFieldSingleFileUpload uses RHF setValue)
  const currentManuscriptUrl = watch('fileSet.Manuscript.url') ?? '';
  const currentTitlePageUrl = watch('fileSet.TitlePage.url') ?? '';

  const hasFileChanged =
    originalManuscriptUrl.current !== currentManuscriptUrl ||
    originalTitlePageUrl.current !== currentTitlePageUrl;
  const needsRerunExtraction = formData.hasRunExtraction === false;

  function getValuesForDraft() {
    const values = getValues();

    // Once extraction is invalidated, preserve that status in every draft save
    // until a successful extraction explicitly sets it back to true.
    if (
      needsRerunExtraction ||
      (hasFileChanged && !extractionWasReRun.current)
    ) {
      extractionNeedsRerun.current = true;
    }

    if (extractionNeedsRerun.current) {
      values.hasRunExtraction = false;
    }

    return values;
  }

  const canExtract = Boolean(
    shouldRunExtraction &&
      draftSubmissionId &&
      submissionContextId &&
      currentManuscriptUrl
  );
  const canExtractTitlePage = Boolean(
    shouldRunExtraction &&
      configs.TitlePage &&
      currentTitlePageUrl &&
      draftSubmissionId &&
      submissionContextId
  );

  const {
    data: extractionData,
    isLoading: extractionLoading,
    isError: extractionError
  } = useQuery({
    queryKey: [
      `details-extraction-manuscript-${draftSubmissionId}`,
      currentManuscriptUrl,
      shouldRunExtraction
    ],
    queryFn: () =>
      fetchExtractedSubmissionMetadata(
        draftSubmissionId,
        submissionContextId,
        currentManuscriptUrl
      ),
    enabled: canExtract,
    retry: 0
  });

  const {
    data: titlePageExtractionData,
    isLoading: titlePageExtractionLoading,
    isError: titlePageExtractionError
  } = useQuery({
    queryKey: [
      `details-extraction-titlepage-${draftSubmissionId}`,
      currentTitlePageUrl,
      shouldRunExtraction
    ],
    queryFn: () =>
      configs.TitlePage && currentTitlePageUrl
        ? fetchExtractedTitlePageMetadata(
            draftSubmissionId,
            submissionContextId,
            currentTitlePageUrl
          )
        : null,
    enabled: canExtractTitlePage,
    retry: 0
  });

  // Track extraction progress
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (shouldRunExtraction && extractionLoading) {
      setExtractionPhase('started');
      setExtractionProgress(0);
      interval = setInterval(() => {
        setExtractionProgress(prev => (prev < 90 ? prev + 1 : prev));
      }, 100);
    } else if (extractionData && !extractionError) {
      setExtractionProgress(100);
      setExtractionPhase('completed');
    } else if (extractionError) {
      setExtractionPhase('failed');
    }
    return () => clearInterval(interval);
  }, [shouldRunExtraction, extractionLoading, extractionData, extractionError]);

  useEffect(() => {
    if (!shouldRunExtraction) return;

    if (extractionLoading) {
      setContentMismatch(false);
      return;
    }

    if (extractionData) {
      setContentMismatch(
        Boolean(canExtractTitlePage) &&
          (extractionData.extracted_data?.authors?.length ?? 0) !== 0
      );
    }
  }, [
    shouldRunExtraction,
    extractionLoading,
    extractionData,
    canExtractTitlePage
  ]);

  // Track title page extraction progress
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (!canExtractTitlePage) {
      setTitlePageExtractionPhase('completed');
      setTitlePageExtractionProgress(100);
    } else if (shouldRunExtraction && titlePageExtractionLoading) {
      setTitlePageExtractionPhase('started');
      setTitlePageExtractionProgress(0);
      interval = setInterval(() => {
        setTitlePageExtractionProgress(prev => (prev < 90 ? prev + 1 : prev));
      }, 100);
    } else if (titlePageExtractionData && !titlePageExtractionError) {
      setTitlePageExtractionProgress(100);
      setTitlePageExtractionPhase('completed');
    } else if (titlePageExtractionError) {
      setTitlePageExtractionPhase('failed');
    }
    return () => clearInterval(interval);
  }, [
    shouldRunExtraction,
    titlePageExtractionLoading,
    titlePageExtractionData,
    titlePageExtractionError,
    canExtractTitlePage
  ]);

  useEffect(() => {
    if (!shouldRunExtraction) return;

    if (titlePageExtractionLoading) {
      setMissingInformation(false);
      return;
    }

    if (titlePageExtractionData) {
      setMissingInformation(
        (titlePageExtractionData.extracted_data?.authors?.length ?? 0) === 0
      );
    }
  }, [
    shouldRunExtraction,
    titlePageExtractionLoading,
    titlePageExtractionData
  ]);

  // Fetch user profile for author data during extraction
  const { data: authorData } = useQuery({
    queryKey: ['user-profile-data'],
    queryFn: () => fetchUserProfileDetails()
  });

  const getFormData = (
    extractedData?: IExtractedSubmissionMetadata
  ): ISubmissionFormData => {
    // Uploaded files are held in React Hook Form until the draft is saved.
    // Use its current values so rerunning extraction does not restore old files
    // from the FormContext snapshot.
    const currentFormData = methods.getValues();
    const currentUser = authorData?.data;
    const currentUserData = {
      email: currentUser?.user.email ?? '',
      firstName: currentUser?.user?.firstName ?? '',
      lastName: currentUser?.user?.lastName ?? '',
      middleName: currentUser?.user?.middleName ?? '',
      country: currentUser?.affiliationDetails?.country ?? '',
      countryCode: currentUser?.affiliationDetails?.countryCode ?? '',
      city: currentUser?.affiliationDetails?.city ?? '',
      id: currentUser?.user?.id ?? '',
      affiliation: currentUser?.affiliationDetails?.organizationName || '',
      orcid: currentUser?.user?.orcid || '',
      ringgoldId: currentUser?.affiliationDetails?.ringgoldId || ''
    };
    const {
      title = 'Untitled',
      abstract = '',
      keywords = []
    } = extractedData ?? {};
    const titlePageExtractedData = (titlePageExtractionData?.extracted_data ||
      {}) as ITitlePageExtractedSubmissionMetadata;
    const { title: titlePageTitle, authors: titlePageAuthors = [] } =
      titlePageExtractedData;
    const metadataTitle =
      !titlePageTitle || titlePageTitle.trim() === '' ? title : titlePageTitle;
    const extractedAuthors = currentTitlePageUrl
      ? titlePageAuthors
      : extractedData?.authors ?? [];
    const filteredAuthors = extractedAuthors
      .filter(author => author.email !== currentUser?.user.email)
      .map(author => {
        const country = author?.country?.[0] ?? '';
        const countryCode = countriesData?.find(
          item => item.country.toLowerCase() === country.toLowerCase()
        )?.countryCode;
        return {
          email: author.email ?? '',
          firstName: author?.first_name ?? '',
          lastName: author?.last_name ?? '',
          middleName: author?.middle_name ?? '',
          affiliation: author.institution?.[0] ?? '',
          country,
          countryCode: countryCode ?? '',
          city: author?.city?.[0] ?? ''
        };
      })
      .filter(author =>
        Object.values(author).some(value => value!.trim() !== '')
      );
    const processedAbstract = Array.isArray(abstract)
      ? abstract[0] || ''
      : abstract === null || abstract === undefined
      ? ''
      : abstract;
    const sanitizedFileSet = currentTitlePageUrl
      ? currentFormData.fileSet
      : (({ TitlePage: _TitlePage, ...fileSet }) => fileSet)(
          currentFormData.fileSet
        );

    return {
      ...currentFormData,
      fileSet: sanitizedFileSet,
      keywords: keywords.map(keyword => ({ text: keyword })),
      metadata: {
        ...currentFormData.metadata,
        title: metadataTitle,
        abstract: processedAbstract,
        keywords,
        authors: [currentUserData, ...filteredAuthors]
      },
      submittingAuthor: currentUserData.email,
      correspondingAuthor: currentUserData.email,
      hasRunExtraction: true
    };
  };

  // When both extractions complete, update form data, show toast, close modal
  useEffect(() => {
    const bothComplete =
      extractionPhase === 'completed' &&
      (titlePageExtractionPhase === 'completed' || !canExtractTitlePage);
    const anyFailed =
      extractionPhase === 'failed' || titlePageExtractionPhase === 'failed';

    if (!shouldRunExtraction) return;

    if (anyFailed) return;

    if (bothComplete) {
      const manuscriptExtractedData =
        extractionData?.extracted_data as IExtractedSubmissionMetadata;
      if (!manuscriptExtractedData) return;

      const hasContentMismatch =
        canExtractTitlePage &&
        (manuscriptExtractedData.authors?.length ?? 0) !== 0;
      const hasMissingInformation =
        canExtractTitlePage &&
        (titlePageExtractionData?.extracted_data?.authors?.length ?? 0) === 0;

      // Completed extraction requests may still contain invalid extracted data.
      // Keep this result in the modal instead of treating it as a success.
      if (hasContentMismatch || hasMissingInformation) {
        setContentMismatch(hasContentMismatch);
        setMissingInformation(hasMissingInformation);
        return;
      }

      const newFormData = getFormData(manuscriptExtractedData);

      updateFormData(newFormData);
      methods.reset(newFormData);

      // Update original refs so file change detection resets
      originalManuscriptUrl.current = currentManuscriptUrl;
      originalTitlePageUrl.current = currentTitlePageUrl;

      // Mark that extraction was re-run successfully
      extractionWasReRun.current = true;
      extractionNeedsRerun.current = false;

      // Close modal and show success toast
      setShowRerunModal(false);
      setShouldRunExtraction(false);
      toast({
        variant: 'success',
        title: 'Submission File(s) Extraction Complete.'
      });
    }
  }, [
    extractionPhase,
    titlePageExtractionPhase,
    shouldRunExtraction,
    canExtractTitlePage,
    extractionData,
    titlePageExtractionData,
    currentTitlePageUrl,
    formData.metadata,
    formData.fileSet,
    currentManuscriptUrl,
    authorData,
    countriesData,
    updateFormData,
    methods,
    toast
  ]);

  const handleRerunClick = () => {
    // Keep the re-run action available if the confirmation modal is cancelled.
    // This is reset to true only after extraction completes successfully.
    extractionNeedsRerun.current = true;
    updateFormData({ hasRunExtraction: false });
    setShowRerunModal(true);
    setExtractionPhase('started');
    setTitlePageExtractionPhase('started');
    setExtractionProgress(0);
    setTitlePageExtractionProgress(0);
    setContentMismatch(false);
    setMissingInformation(false);
    setShouldRunExtraction(false);
  };

  const startRerunExtraction = () => {
    setShouldRunExtraction(true);
  };

  const closeRerunModal = () => {
    setShowRerunModal(false);
    setShouldRunExtraction(false);
    setExtractionPhase('started');
    setTitlePageExtractionPhase('started');
  };

  const handleModalClose = () => {
    // Only allow closing if extraction is not running
    if (!shouldRunExtraction) {
      closeRerunModal();
    }
  };

  // ---------- Validation Method ----------
  const validateFigures = (data: ISubmissionFormData): boolean => {
    let isValid = true;

    // Clear any previous errors on figures
    clearErrors('fileSet.Figures');

    const figures = data.fileSet?.Figures;

    // If no figures array -> valid
    if (!figures || figures.length === 0) return true;

    // Check if all figures are optional (completely empty)
    const allEmpty = figures.every((fig: any) => {
      const noCaption = !fig.caption || fig.caption.trim() === '';
      const noFile = !fig.file;
      return noCaption && noFile;
    });

    if (allEmpty) {
      return true;
    }

    // Track duplicates (case-insensitive, trimmed)
    const seenCaptions = new Set<string>();

    // Validate each figure
    figures.forEach((fig: any, idx: number) => {
      const captionRaw = fig.caption ?? '';
      const caption = captionRaw.trim();
      if (caption !== '') {
        const normalized = caption.toLowerCase();
        if (seenCaptions.has(normalized)) {
          methods.setError(`fileSet.Figures.${idx}.caption`, {
            type: 'manual',
            message: 'Duplicate caption not allowed for figures.'
          });
          isValid = false;
        } else {
          seenCaptions.add(normalized);
        }
      }
    });

    return isValid;
  };

  const validateGraphicalAbstractImage = (
    data: ISubmissionFormData
  ): boolean => {
    if (!configs.GraphicalAbstractImage) {
      return true;
    }

    let isValid = true;

    clearErrors('fileSet.GraphicalAbstractImage');

    const images = data.fileSet?.GraphicalAbstractImage;

    if (!images || images.length === 0) return true;

    const allEmpty = images.every((img: any) => {
      const noCaption = !img.caption || img.caption.trim() === '';
      const noFile = !img.file;
      return noCaption && noFile;
    });

    if (allEmpty) return true;

    const seenCaptions = new Set<string>();

    images.forEach((img: any, idx: number) => {
      const captionRaw = img.caption ?? '';
      const caption = captionRaw.trim();
      if (caption !== '') {
        const normalized = caption.toLowerCase();
        if (seenCaptions.has(normalized)) {
          methods.setError(`fileSet.GraphicalAbstractImage.${idx}.caption`, {
            type: 'manual',
            message:
              'Duplicate caption not allowed for graphical abstract images.'
          });
          isValid = false;
        } else {
          seenCaptions.add(normalized);
        }
      }
    });

    return isValid;
  };

  const validateTables = (data: ISubmissionFormData): boolean => {
    let isValid = true;

    // Clear any previous errors on tables
    clearErrors('fileSet.Tables');

    const tables = data.fileSet?.Tables;

    // If no tables array -> valid
    if (!tables || tables.length === 0) return true;

    // Check if all tables are optional (completely empty)
    const allEmpty = tables.every((table: any) => {
      const noCaption = !table.caption || table.caption.trim() === '';
      const noFile = !table.file;
      return noCaption && noFile;
    });

    if (allEmpty) {
      return true;
    }

    // Track duplicates (case-insensitive, trimmed)
    const seenCaptions = new Set<string>();

    // Validate each table
    tables.forEach((table: any, idx: number) => {
      const captionRaw = table.caption ?? '';
      const caption = captionRaw.trim();
      if (caption !== '') {
        const normalized = caption.toLowerCase();
        if (seenCaptions.has(normalized)) {
          methods.setError(`fileSet.Tables.${idx}.caption`, {
            type: 'manual',
            message: 'Duplicate caption not allowed for tables.'
          });
          isValid = false;
        } else {
          seenCaptions.add(normalized);
        }
      }
    });

    return isValid;
  };
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, scrollToFirstError)}>
        <Flex
          flexDirection="column"
          backgroundColor="surfaceSecondaryFill"
          minH="100vh"
        >
          <SidebarLayout
            sidebar={
              <SubmissionSidebar
                currentStep={currentStep}
                onNavigate={step => {
                  goToStep(step);
                  onNavigateSave();
                }}
              />
            }
          >
            <StepHeader
              currentStep={currentStep}
              totalSteps={totalSteps}
              heading={
                reviewSubmissionInformation?.header ?? 'Submission Information'
              }
              description={
                reviewSubmissionInformation?.subHeader ??
                'Review and edit the extracted content, such as the submission’s title, abstract and keywords, ensuring that all required information is included.'
              }
            />
            <Flex px={{ base: 3, md: 0 }} mb={4}>
              <Accordion
                chakraAccordionProps={{
                  width: '100%',
                  defaultIndex: [0],
                  allowToggle: true
                }}
                items={[
                  {
                    title: (
                      <Text textStyle="headingLine">Submission Files</Text>
                    ),
                    content: (
                      <Flex flexDirection="column" gap={4}>
                        {/* --- Title Page upload --- */}

                        {/* --- Manuscript upload --- */}
                        {configs.Manuscript && (
                          <FormFieldSingleFileUpload
                            submissionContextId={submissionContextId}
                            config={configs.Manuscript}
                            methods={methods}
                            submissionId={draftSubmissionId}
                            width={{ base: '100%', md: '50%' }}
                          />
                        )}
                        {configs.TitlePage && (
                          <FormFieldSingleFileUpload
                            submissionContextId={submissionContextId}
                            config={configs.TitlePage}
                            methods={methods}
                            submissionId={draftSubmissionId}
                            width={{ base: '100%', md: '50%' }}
                            TitlePageSampleDownload={null}
                          />
                        )}
                        <Flex justifyContent="flex-end" width="100%">
                          {/* --- Rerun Extraction --- */}
                          <Button
                            variant="primary"
                            leftIcon={<FontAwesomeIcon icon={faRotateRight} />}
                            onClick={handleRerunClick}
                            disabled={
                              (!hasFileChanged && !needsRerunExtraction) ||
                              !currentManuscriptUrl ||
                              (Boolean(configs.TitlePage) &&
                                !currentTitlePageUrl)
                            }
                            width="fit-content"
                          >
                            Rerun Extraction
                          </Button>
                        </Flex>
                      </Flex>
                    )
                  }
                ]}
              />
            </Flex>
            <Flex flexDirection="column" gap={8}>
              <Flex flexDirection="column" gap={8} alignItems="flex-start">
                {/* ----- Submission ----- */}
                <Flex flexDirection="column" gap={6} width="100%">
                  {configs.FocusIssue && (
                    <FocusIssue
                      config={configs.FocusIssue}
                      guidelineLink={guidelineLink}
                    />
                  )}

                  {/* --- Title --- */}
                  {configs.Title && (
                    <OutlineCard width="100%" padding={4}>
                      <Title
                        config={configs.Title}
                        isMetadataExtracted={false}
                        editTitle={
                          reviewSubmissionInformation?.editTitle ??
                          'Edit Extracted Title'
                        }
                        data-cy="title-component"
                      />
                    </OutlineCard>
                  )}

                  {/* --- EnglishTitle --- */}
                  {configs.EnglishTitle && (
                    <CustomFormFieldComponent config={configs.EnglishTitle} />
                  )}

                  {configs.Subtitle && (
                    <OutlineCard width="100%" padding={4}>
                      <GenericRichTextInput config={configs.Subtitle} />
                    </OutlineCard>
                  )}

                  {configs.EnglishSubtitle && (
                    <CustomFormFieldComponent
                      config={configs.EnglishSubtitle}
                    />
                  )}
                  {/* --- Abstract --- */}
                  {configs.Abstract && (
                    <OutlineCard width="100%" padding={4}>
                      <Abstract
                        config={configs.Abstract}
                        isMetadataExtracted={false}
                        editAbstract={
                          reviewSubmissionInformation?.editAbstract ??
                          'Edit Extracted Abstract'
                        }
                        data-cy="abstract-component"
                      />
                    </OutlineCard>
                  )}
                  {/* --- EnglishAbstract --- */}
                  {configs.EnglishAbstract && (
                    <CustomFormFieldComponent
                      config={configs.EnglishAbstract}
                    />
                  )}

                  {/* --- Keywords --- */}
                  {configs.Keywords && (
                    <Box>
                      <Keywords
                        config={configs.Keywords}
                        isMetadataExtracted={false}
                        editKeywords={
                          reviewSubmissionInformation?.editKeywords ??
                          'Edit Extracted Keywords'
                        }
                        data-cy="keywords-component"
                      />
                    </Box>
                  )}

                  {configs.EnglishKeywords && (
                    <Box>
                      <CustomFormFieldComponent
                        config={configs.EnglishKeywords}
                      />
                    </Box>
                  )}

                  {configs.SubmissionGuidelines && (
                    <OutlineCard width="100%" padding={4}>
                      <CustomFormFieldComponent
                        config={configs.SubmissionGuidelines}
                      />
                    </OutlineCard>
                  )}

                  {configs.Category && (
                    <OutlineCard width="100%" padding={4}>
                      <Flex
                        flexDirection="column"
                        gap={4}
                        width={{ base: '100%', md: '50%' }}
                        mb={2}
                      >
                        <CustomFormFieldComponent config={configs.Category} />

                        {configs.CategoryOther && (
                          <CustomFormFieldComponent
                            config={configs.CategoryOther}
                            placeholder="Please enter other category"
                          />
                        )}
                      </Flex>
                    </OutlineCard>
                  )}

                  {/* ----- Source File Upload ----- */}
                  {configs.SourceFiles && (
                    <SourceFileUpload
                      submissionId={draftSubmissionId}
                      config={configs.SourceFiles}
                      data-cy="sourcefiles-upload-component"
                    />
                  )}

                  {configs.StandardReportingGuidelines && (
                    <StandardReportingGuidelines
                      config={configs.StandardReportingGuidelines}
                      CONSORTFilesConfig={configs.CONSORTFiles}
                      ARRIVEFilesConfig={configs.ARRIVEFiles}
                      submissionId={draftSubmissionId}
                      StandardReportingGuidelinesDetails={
                        configs.StandardReportingGuidelinesDetails
                      }
                      StandardReportingGuidelinesFileDetails={
                        configs.StandardReportingGuidelinesFileDetails
                      }
                      StandardReportingGuidelinesTextConfig={
                        configs.StandardReportingGuidelinesText ?? null
                      }
                    />
                  )}

                  {/* ----- Supplementary Upload ----- */}
                  {configs.SupplementaryUpload && (
                    <SupplementaryUpload
                      submissionId={draftSubmissionId}
                      config={configs.SupplementaryUpload}
                      data-cy="supplementary-upload-component"
                    />
                  )}

                  {/* ----- Additional Supplementary Upload ----- */}
                  {configs.AdditionalSupplementary && (
                    <AdditionalSupplementaryUpload
                      submissionId={draftSubmissionId}
                      config={configs.AdditionalSupplementary}
                      data-cy="additional-supplementary-upload-component"
                    />
                  )}

                  {configs.CoverLetter && (
                    <CoverLetter
                      submissionId={draftSubmissionId}
                      config={configs.CoverLetter}
                    />
                  )}

                  {configs.Figures && (
                    <Figures
                      config={configs.Figures}
                      submissionId={draftSubmissionId}
                    />
                  )}
                  {configs.GraphicalAbstractImage && (
                    <GraphicalAbstractImage
                      config={configs.GraphicalAbstractImage}
                      submissionId={draftSubmissionId}
                    />
                  )}

                  {configs.Tables && (
                    <Tables
                      config={configs.Tables}
                      submissionId={draftSubmissionId}
                    />
                  )}

                  {configs.NumberOfFigures && (
                    <OutlineCard padding={4}>
                      <Flex
                        flexDirection="column"
                        gap={4}
                        width={{ base: '100%', md: '50%' }}
                        mb={2}
                      >
                        <FormFieldInteger config={configs.NumberOfFigures} />
                      </Flex>
                    </OutlineCard>
                  )}

                  {configs.NumberOfColorFigures && (
                    <OutlineCard padding={4}>
                      <Flex
                        flexDirection="column"
                        gap={4}
                        width={{ base: '100%', md: '50%' }}
                        mb={2}
                      >
                        <FormFieldInteger
                          config={configs.NumberOfColorFigures}
                        />
                      </Flex>
                    </OutlineCard>
                  )}

                  {configs.NumberOfTables && (
                    <OutlineCard padding={4}>
                      <Flex
                        flexDirection="column"
                        gap={4}
                        width={{ base: '100%', md: '50%' }}
                        mb={2}
                      >
                        <FormFieldInteger config={configs.NumberOfTables} />
                      </Flex>
                    </OutlineCard>
                  )}
                  {configs.NumberOfWords && (
                    <OutlineCard padding={4}>
                      <Flex
                        flexDirection="column"
                        gap={4}
                        width={{ base: '100%', md: '50%' }}
                        mb={2}
                      >
                        <FormFieldInteger config={configs.NumberOfWords} />
                      </Flex>
                    </OutlineCard>
                  )}

                  {configs.GeneralClassifications && (
                    <CustomFormFieldComponent
                      config={configs.GeneralClassifications}
                    />
                  )}
                </Flex>
              </Flex>
            </Flex>
          </SidebarLayout>
        </Flex>
        {/* ----- Footer Buttons ----- */}
        <Flex
          padding={{ base: 3, md: 4 }}
          bottom={0}
          zIndex={1000}
          justifyContent="center"
          position="sticky"
          bgColor="surfacePrimaryFill"
          marginX="auto"
        >
          {/* Desktop */}
          <Flex
            width="100%"
            justifyContent={prevStep <= 2 ? 'space-between' : 'flex-end'}
            display={{ base: 'none', md: 'flex' }}
          >
            {prevStep <= 2 && (
              <BackToUpload
                submissionId={draftSubmissionId}
                goToFirstStep={goToFirstStep}
                submissionContextId={submissionContextId}
                isDisabled={
                  isDisabledDraft || isDisableNextButton || isFileUploading
                }
              />
            )}
            <Flex gap={4}>
              {draftSubmissionId && (
                <DeleteDraft
                  submissionId={draftSubmissionId}
                  submissionContextId={submissionContextId}
                  isDisabled={
                    isDisabledDraft || isDisableNextButton || isFileUploading
                  }
                />
              )}
              <Button
                variant="secondary"
                leftIcon={<FontAwesomeIcon icon={faSave} />}
                onClick={() => onSaveDraft()}
                disabled={
                  isDisabledDraft || isDisableNextButton || isFileUploading
                }
              >
                Save Draft
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={
                  isDisableNextButton || isFileUploading || isNavigating
                }
              >
                Next
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </Flex>
          </Flex>

          {/* Mobile */}
          <Flex
            width="100%"
            direction="column"
            gap={2}
            display={{ base: 'flex', md: 'none' }}
          >
            <Flex gap={2}>
              {draftSubmissionId && (
                <DeleteDraft
                  submissionId={draftSubmissionId}
                  submissionContextId={submissionContextId}
                  isDisabled={
                    isDisabledDraft || isDisableNextButton || isFileUploading
                  }
                />
              )}
              <Button
                variant="secondary"
                leftIcon={<FontAwesomeIcon icon={faSave} />}
                onClick={() => onSaveDraft()}
                flex={1}
                disabled={
                  isDisabledDraft || isDisableNextButton || isFileUploading
                }
              >
                Save Draft
              </Button>
            </Flex>

            <Button
              variant="primary"
              type="submit"
              width="100%"
              disabled={isDisableNextButton || isFileUploading || isNavigating}
            >
              Next
              <FontAwesomeIcon icon={faChevronRight} />
            </Button>
            {prevStep <= 2 && (
              <BackToUpload
                submissionId={draftSubmissionId}
                goToFirstStep={goToFirstStep}
                submissionContextId={submissionContextId}
                isDisabled={
                  isDisabledDraft || isDisableNextButton || isFileUploading
                }
              />
            )}
          </Flex>
        </Flex>

        {/* ----- Rerun Extraction Confirmation Modal ----- */}
        <Modal
          isOpen={showRerunModal}
          onClose={handleModalClose}
          width={!shouldRunExtraction ? '490px' : '800px'}
          closeButtonProps={{
            hidden: true
          }}
        >
          {!shouldRunExtraction ? (
            <Flex
              padding={8}
              gap={6}
              flexDirection="column"
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <FontAwesomeIcon
                fontSize="40px"
                icon={faTriangleExclamation}
                color="orange"
              />
              <Flex gap={2} flexDirection="column" justifyContent="flex-start">
                <Text textStyle="headingGroup">Re-run extraction?</Text>
                <Text alignItems="flex-start">
                  Re-running the extraction process will replace the current
                  field data in your submission. This action cannot be undone.
                </Text>
              </Flex>
              <Flex gap={2} justifyContent="flex-end" width="100%">
                <Button
                  variant="secondary"
                  onClick={() => setShowRerunModal(false)}
                  borderRadius="small"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={startRerunExtraction}
                  borderRadius="small"
                >
                  Re-Run Extraction
                </Button>
              </Flex>
            </Flex>
          ) : (
            <Flex padding={8} flexDirection="column" gap={4}>
              <MetadataExtraction
                phase={extractionPhase}
                manualOverride={false}
                goToNextStep={closeRerunModal}
                goToPreviousStep={closeRerunModal}
                onRetry={startRerunExtraction}
                metadataExtraction={
                  submissionContext?.config.cms.authorSubmissionForm
                    .metadataExtraction
                }
                extractionProgress={extractionProgress}
                titlePageExtractionProgress={titlePageExtractionProgress}
                titlePageExtractionPhase={titlePageExtractionPhase}
                titlePageFile={Boolean(
                  configs.TitlePage && currentTitlePageUrl
                )}
                contentMismatch={contentMismatch}
                missingInformation={missingInformation}
                TitlePageData={
                  titlePageExtractionData?.extracted_data?.authors ?? []
                }
                MenuscriptData={extractionData?.extracted_data?.authors ?? []}
                keywordSuggestionEnabled={false}
                keywordSuggestionPhase="started"
                keywordSuggestionProgress={0}
              />
              {(extractionPhase === 'completed' ||
                extractionPhase === 'failed') &&
                (titlePageExtractionPhase === 'completed' ||
                  titlePageExtractionPhase === 'failed' ||
                  !canExtractTitlePage) && (
                  <Flex justifyContent="center" width="100%">
                    <Button
                      variant="primary"
                      onClick={closeRerunModal}
                      borderRadius="small"
                    >
                      Back to Submission
                    </Button>
                  </Flex>
                )}
            </Flex>
          )}
        </Modal>
      </form>
    </FormProvider>
  );
};

export default DetailsStep;
